import { phone } from "$lib/yup/util/phone-number"
import { taxNumberSchema } from "$lib/yup/util/tax-number"
import { addressFactory } from "@tastyworks/tastyworks-api/factories/address"
import { boolean, object, string, type InferType } from "yup"
import { addressSchema } from "../address"

// TODO: Move this; probably to tastyworks-api when ready
export const INDUSTRIES = [
  "Agriculture, Forestry, and Fishing",
  "Construction",
  "Finance, Insurance, or Real Estate",
  "General Services",
  "Manufacturing",
  "Mining",
  "Public Administration",
  "Retail Trade",
  "Transportation or Utilities",
  "Wholesale Trade",
] as const

const secretarySchema = string().when(["hasSecretary"], {
  is: true,
  then: (schema) => schema.required(),
})

export const businessBasicsSchema = object({
  legalName: string().required(),
  businessNature: string().required().oneOf(INDUSTRIES),
  taxNumber: taxNumberSchema,
  phoneNumber: phone().required(),
})

export const primaryBusinessAddressSchema = object({
  physicalAddress: addressSchema,
})

export const businessSameMailingAddressSchema = object({
  mailingAddressSameAsPrimary: boolean().required(),
})

export const businessMailingAddressDetailsSchema = object({
  mailingAddress: addressSchema,
})

export const companySecretarySchema = object({
  hasSecretary: boolean().required(),
  secretaryFirstName: secretarySchema,
  secretaryLastName: secretarySchema,
})

export const businessInformationSchema = object({
  basics: businessBasicsSchema,
  primaryAddress: primaryBusinessAddressSchema,
  businessSameMailingAddress: businessSameMailingAddressSchema,
  businessMailingAddressDetails: businessMailingAddressDetailsSchema,
  companySecretary: companySecretarySchema,
})

export type CompanySecretaryData = InferType<typeof companySecretarySchema>
export type PrimaryBusinessAddressData = InferType<
  typeof primaryBusinessAddressSchema
>

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const validBusinessInformation = {
    basics: {
      legalName: "Entity Name",
      businessNature: INDUSTRIES[0],
      taxNumber: "123456789",
      phoneNumber: {
        countryCode: "USA",
        phoneNumber: "2164020202",
      },
    },
    primaryAddress: {
      physicalAddress: addressFactory.build(),
    },
    businessSameMailingAddress: {
      mailingAddressSameAsPrimary: true,
    },
    businessMailingAddressDetails: {
      mailingAddress: addressFactory.build(),
    },
    companySecretary: {
      hasSecretary: true,
      secretaryFirstName: "Bob",
      secretaryLastName: "Builder",
    },
  }

  describe("businessInformationSchema", () => {
    it("successfully validates required business info", () => {
      expect(
        businessInformationSchema.isValidSync(validBusinessInformation)
      ).toBe(true)
    })

    it("requires legal name", () => {
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          basics: {
            ...validBusinessInformation.basics,
            legalName: "",
          },
        })
      ).toBe(false)
    })

    it("requires industry from list of options", () => {
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          basics: {
            ...validBusinessInformation.basics,
            businessNature: "Not in the list",
          },
        })
      ).toBe(false)
    })

    it("expects tax number to be exactly 9 digits", () => {
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          basics: {
            ...validBusinessInformation.basics,
            taxNumber: "12345678",
          },
        })
      ).toBe(false)
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          basics: {
            ...validBusinessInformation.basics,
            taxNumber: "1234567890",
          },
        })
      ).toBe(false)
    })

    it("requires valid country code and phone number", () => {
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          basics: {
            ...validBusinessInformation.basics,
            phoneNumber: {
              countryCode: "USA",
              phoneNumber: "555 888 0000",
            },
          },
        })
      ).toBe(false)
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          basics: {
            ...validBusinessInformation.basics,
            phoneNumber: "",
          },
        })
      ).toBe(false)
    })

    it("requires valid physical address", () => {
      expect(
        businessInformationSchema.isValidSync({
          ...validBusinessInformation,
          primaryAddress: {
            physicalAddress: {
              ...addressFactory.build(),
              streetOne: "",
            },
          },
        })
      ).toBe(false)
    })
  })

  describe("businessSameMailingAddressSchema", () => {
    it("requires mailingAddressSameAsPrimary boolean", () => {
      expect(
        businessSameMailingAddressSchema.isValidSync({
          mailingAddressSameAsPrimary: true,
        })
      ).toBe(true)

      expect(
        businessSameMailingAddressSchema.isValidSync({
          mailingAddressSameAsPrimary: false,
        })
      ).toBe(true)

      expect(businessSameMailingAddressSchema.isValidSync({})).toBe(false)
    })
  })

  describe("businessMailingAddressDetailsSchema", () => {
    it("requires a valid mailing address", () => {
      expect(
        businessMailingAddressDetailsSchema.isValidSync({
          mailingAddress: addressFactory.build(),
        })
      ).toBe(true)

      expect(
        businessMailingAddressDetailsSchema.isValidSync({
          mailingAddress: {
            ...addressFactory.build(),
            streetOne: "",
          },
        })
      ).toBe(false)
    })
  })
}
